import { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInWithCustomToken } from "firebase/auth";
import { Notification } from "../core/notification_handler";
import { auth } from "../firebase/config";
import { vespidFunctionsAPI } from "../core/api";
import usePartnerDataHandler from "../hooks/partner_data_handler_hook";
import * as loadingAnimations from "../assets/animations/Loading.json";
import Lottie from "react-lottie";
import { authenticationAPI, PartnerConflictError } from "../core/authentication_handler";
import { ReactComponent as GoogleSignIn } from "../assets/icons/GoogleSignIn.svg";
import { ReactComponent as AppleSignIn } from "../assets/icons/AppleSignIn.svg";
import useDeviceTypeHandler from "../hooks/device_type_handler_hook";
import PasswordVisibility from "../components/PasswordVisibility";
import LoadingStatus from "../components/LoadingStatus";
import ConfirmPartnerDialog from "../components/ConfirmPartnerDialog";
import Button from "../components/Button";
import axios from "axios";

const Login = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [partnerConflict, setPartnerConflict] = useState<{
    currentPartner: { id: string; name: string };
    newPartnerId: string;
  } | null>(null);
  const [isReplacingPartner, setIsReplacingPartner] = useState(false);
  const form = useRef();
  const navigate = useNavigate();
  const { partnerData, isPartnerDataLoading, searchParams } = usePartnerDataHandler();
  const deviceType = useDeviceTypeHandler();

  const handlePartnerConflict = (currentPartner: { id: string; name: string }, newPartnerId: string) => {
    setPartnerConflict({ currentPartner, newPartnerId });
  };

  const handleConfirmPartnerReplace = async () => {
    if (!partnerConflict) return;
    
    setIsReplacingPartner(true);
    try {
      await authenticationAPI.completeOAuthWithReplacement(partnerConflict.newPartnerId);
      Notification.success("Partner association updated successfully!");
      setPartnerConflict(null);
      navigate("/install");
    } catch (error) {
      Notification.error("Failed to update partner association. Please try again.");
    } finally {
      setIsReplacingPartner(false);
    }
  };

  const handleCancelPartnerReplace = () => {
    setPartnerConflict(null);
    Notification.success("Your login is successful! You can now use the Vespid app.");
    navigate("/install");
  };

  const loginHandler = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await vespidFunctionsAPI.postPublic("/account/login", {
        email: e.target.email.value,
        password: e.target.password.value,
      });

      if (!response.data?.custom_token) {
        throw new Error("Login succeeded but no authentication token was returned. Please try again or contact support.");
      }
      await signInWithCustomToken(auth, response.data.custom_token);
      
      // Complete user setup (for legacy users) and tag to partner
      try {
        await vespidFunctionsAPI.post("/account/oauth/complete", {
          partner_id: partnerData?.id || undefined,
        });
        Notification.success("Your login is successful! You can now use the Vespid app.");
        navigate("/install");
      } catch (oauthError: any) {
        // Handle 400 failed-precondition partner conflict
        if (axios.isAxiosError(oauthError) && 
            oauthError.response?.status === 400 && 
            oauthError.response?.data?.error?.code === 'failed-precondition') {
          const currentPartner = oauthError.response.data?.current_partner;
          if (currentPartner && partnerData?.id) {
            handlePartnerConflict(currentPartner, partnerData.id);
            return;
          }
        }
        throw oauthError;
      }
    } catch (error: any) {
      // Only handle Firebase auth errors here - API errors are handled by the interceptor
      if (error.code) {
        authenticationAPI.authErrorHandler(error.code);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const googleSignIn = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await authenticationAPI.googleSignIn(navigate, partnerData?.id);
    } catch (error: any) {
      if (error instanceof PartnerConflictError) {
        handlePartnerConflict(error.currentPartner, error.newPartnerId);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const appleSignIn = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await authenticationAPI.appleSignIn(navigate, partnerData?.id);
    } catch (error: any) {
      if (error instanceof PartnerConflictError) {
        handlePartnerConflict(error.currentPartner, error.newPartnerId);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    const passwordInput = document.getElementById(
      "passwordInput"
    ) as HTMLInputElement;
    if (passwordInput.type === "password") {
      passwordInput.type = "text";
    } else {
      passwordInput.type = "password";
    }
    setPasswordVisible(!passwordVisible);
  };

  return (
    <div className="font-primaryFont font-light flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="flex flex-col-reverse md:flex-row">
        {isPartnerDataLoading ? (
          <div className="max-w-sm md:max-w-md mx-auto">
            <Lottie
              options={{
                animationData: loadingAnimations,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
              height={100}
              width={100}
              className="w-1/2 md:w-4/12"
            />
            <h1 className="mt-4 text-center text-2xl">
              Please wait while we are fetching your registration data...
            </h1>
          </div>
        ) : (
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <h1 className="mt-4 text-center text-2xl">
              Login your account here for {partnerData?.name || "the event"}
            </h1>
            <form
              id="register-form"
              ref={form}
              onSubmit={loginHandler}
              className="mt-4 flex flex-col justify-center items-center w-full border shadow-lg px-4 py-4 rounded-lg max-w-6 space-y-6"
            >
              <input
                type="email"
                name="email"
                placeholder="Email"
                required
                data-testid="login-email"
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primaryYellow sm:text-sm/6"
              ></input>
              <div className="relative w-full">
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  required
                  data-testid="login-password"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primaryYellow sm:text-sm/6"
                  id="passwordInput"
                />
                <button
                  type="button"
                  onClick={() => {
                    togglePasswordVisibility();
                  }}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 cursor-pointer"
                >
                  <PasswordVisibility hide={passwordVisible} />
                </button>
              </div>
              <Button
                isDisabled={isLoading}
                htmlType="submit"
                variant="primary"
                type="standard"
                fullWidth
                data-testid="login-submit"
              >
                <LoadingStatus isLoading={isLoading} defaultText="Login" />
              </Button>
              <div>
                {" "}
                ------------------------------ or ------------------------------{" "}
              </div>
              <div className="flex flex-col mt-4">
                <button
                  disabled={isLoading}
                  onClick={googleSignIn}
                  type="button"
                  className="flex w-full justify-center px-3  shadow-xs"
                >
                  <GoogleSignIn className="w-64 " />
                </button>
                {deviceType !== "android" ? (
                  <button
                    disabled={isLoading}
                    onClick={appleSignIn}
                    type="button"
                    className="flex w-full justify-center px-3  shadow-xs"
                  >
                    <AppleSignIn className="w-64 " />
                  </button>
                ) : null}
              </div>

              <p className="mt-2 font-light text-center">
                New to Vespid?
                <Link
                  to={`/register?${searchParams}`}
                  rel="noreferrer noopener"
                  className=" text-primaryOrange"
                >
                  {" "}
                  Register
                </Link>{" "}
              </p>
            </form>
          </div>
        )}
      </div>
      
      <ConfirmPartnerDialog
        isOpen={partnerConflict !== null}
        currentPartnerName={partnerConflict?.currentPartner?.name || "Unknown Partner"}
        newPartnerName={partnerData?.name || "New Partner"}
        onConfirm={handleConfirmPartnerReplace}
        onCancel={handleCancelPartnerReplace}
        isLoading={isReplacingPartner}
      />
    </div>
  );
};

export default Login;
